import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import useAiQuota from '@/hooks/useAiQuota';
import {
  User, MapPin, Calendar, Stethoscope, BookOpen, ExternalLink,
  Plus, Trash2, Save, Loader2, CheckCircle, XCircle, Clock, AlertCircle, ToggleRight, Star, Sparkles,
  Camera, Upload, Wallet, UserX, Crown, CreditCard, Download, Lock, AlertTriangle, Copy, Share2, MessageCircle,
  FileText, Image, Send, SpellCheck, Bell, BellOff, BarChart3, TrendingUp, Eye, EyeOff, Phone, Search,
  Newspaper, History, Check, X, Edit3, RefreshCw, ChevronDown, QrCode, Zap, Gift, Ticket, ClipboardList
} from 'lucide-react';
import BookingChat from '@/components/BookingChat';
import PinSettings from '@/components/dashboard/PinSettings';
import ProfilePublishedCelebration from '@/components/dashboard/ProfilePublishedCelebration';
import MonogramAvatar from '@/components/MonogramAvatar';
import VoiceMeter from '@/components/dashboard/VoiceMeter';
import ClinicSearchPicker from '@/components/ClinicSearchPicker';
import ClinicMap from '@/components/ClinicMap';
import HospitalAttachmentsSection from '@/components/dashboard/HospitalAttachmentsSection';
import {
  Field, ShareUrlBar, PushNotificationToggle, RedeemCouponCard,
  VoiceAuthenticityNotice, ChangePasswordSection,
  WhatsNewLaunchBanner, SetupBlocker, WebsiteScoreWidget,
  TierBadgeWithUpgrade, UpgradeBanner, SubscriptionWarning,
} from '@/components/dashboard/widgets/SharedWidgets';

const STATUS_COLORS = {
  available: 'bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-200',
  tentative: 'bg-amber-100 text-amber-900 border-amber-300 hover:bg-amber-200',
  blocked: 'bg-red-100 text-red-800 border-red-200 hover:bg-red-200',
  manual_booked: 'bg-blue-100 text-blue-900 border-blue-300 hover:bg-blue-200',
  booked: 'bg-sky-100 text-sky-800 border-sky-200 cursor-default',
};
const STATUS_DOT = { available: 'bg-emerald-400', tentative: 'bg-amber-400', blocked: 'bg-red-400', manual_booked: 'bg-blue-500', booked: 'bg-sky-400' };
const STATUS_LABEL = { available: 'Available', tentative: 'Tentative', blocked: 'Blocked by me', manual_booked: 'Manual Booked by me', booked: 'Patient Booked' };
const BLOCK_COLORS_M = { opd: 'bg-emerald-50 border-emerald-200 text-emerald-700', surgery: 'bg-red-50 border-red-200 text-red-700', consultation: 'bg-sky-50 border-sky-200 text-sky-700' };
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAY_LABELS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

export default function SlotManagerTab() {
  const [view, setView] = useState('monthly'); // monthly | daily
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [currentMonth, setCurrentMonth] = useState(() => { const d = new Date(); return { year: d.getFullYear(), month: d.getMonth() + 1 }; });
  const [schedules, setSchedules] = useState([]);
  const [locations, setLocations] = useState([]);
  // Leave-day map for the currently-visible month. Keys are ISO dates,
  // values are the note ("" when blank). Fetched fresh whenever the
  // month changes so ticking a checkbox reflects immediately.
  const [leaveMap, setLeaveMap] = useState({});
  const [leaveBusy, setLeaveBusy] = useState({}); // per-date spinner
  const [showRangeModal, setShowRangeModal] = useState(false);

  useEffect(() => {
    api.get('/dashboard/schedules').then(r => setSchedules(r.data)).catch(() => {});
    api.get('/dashboard/locations').then(r => setLocations(r.data)).catch(() => {});
  }, []);

  // Fetch leaves for the visible month range (with 7-day padding on
  // each side so month-jump animations don't briefly show empty cells).
  const fetchLeaves = useCallback(() => {
    const y = currentMonth.year;
    const m = currentMonth.month;
    const from = new Date(y, m - 1, 1);
    const to = new Date(y, m, 0);
    from.setDate(from.getDate() - 7);
    to.setDate(to.getDate() + 7);
    const fmt = (d) => d.toISOString().slice(0, 10);
    api.get('/dashboard/leaves', { params: { from_date: fmt(from), to_date: fmt(to) } })
      .then(r => {
        const next = {};
        (r.data.leaves || []).forEach(l => { next[l.date] = l.note || ''; });
        setLeaveMap(next);
      })
      .catch(() => setLeaveMap({}));
  }, [currentMonth]);
  useEffect(() => { fetchLeaves(); }, [fetchLeaves]);

  const toggleLeaveDay = async (dateStr) => {
    if (leaveBusy[dateStr]) return;
    setLeaveBusy(s => ({ ...s, [dateStr]: true }));
    try {
      if (dateStr in leaveMap) {
        await api.delete(`/dashboard/leaves/${dateStr}`);
        setLeaveMap(m => { const n = { ...m }; delete n[dateStr]; return n; });
      } else {
        await api.post('/dashboard/leaves', { dates: [dateStr] });
        setLeaveMap(m => ({ ...m, [dateStr]: '' }));
      }
    } catch (_) { /* silent — UI shows unchanged state */ }
    finally { setLeaveBusy(s => { const n = { ...s }; delete n[dateStr]; return n; }); }
  };

  const applyLeaveRange = async (from_date, to_date) => {
    try {
      await api.post('/dashboard/leaves', { from_date, to_date });
      fetchLeaves();
      setShowRangeModal(false);
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || 'Could not mark leave range');
    }
  };

  const getLocName = (id) => locations.find(l => l.id === id)?.name || '?';

  const goToDay = (dateStr) => {
    setSelectedDate(dateStr);
    setView('daily');
  };

  const changeMonth = (dir) => {
    setCurrentMonth(prev => {
      let m = prev.month + dir;
      let y = prev.year;
      if (m > 12) { m = 1; y++; }
      if (m < 1) { m = 12; y--; }
      return { year: y, month: m };
    });
  };

  return (
    <div className="space-y-4" data-testid="slot-manager-tab">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="text-lg font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Slot Manager</h3>
          <p className="text-xs text-slate-500 mt-0.5">Manage your daily availability — what patients see when they try to book.</p>
        </div>
        <div className="flex gap-1 bg-white rounded-xl border border-slate-200 p-1" data-testid="view-switcher">
          {[{k:'monthly',l:'Month'},{k:'daily',l:'Day'}].map(v => (
            <button key={v.k} onClick={() => setView(v.k)}
              className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-colors ${view === v.k ? 'bg-sky-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
              data-testid={`view-${v.k}`}>
              {v.l}
            </button>
          ))}
        </div>
      </div>

      {/* IMPORTANT NOTE — large, eye-catching */}
      {view === 'daily' && (
        <div className="bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 border-2 border-amber-300 rounded-2xl p-5" data-testid="slot-instructions">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-200 flex items-center justify-center flex-shrink-0">
              <Calendar size={20} className="text-amber-800" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-amber-900 uppercase tracking-wide" style={{ fontFamily: 'Outfit, sans-serif' }}>How to manage slots</p>
              <p className="text-base font-semibold text-slate-900 mt-1.5 leading-snug" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <span className="bg-amber-200 px-2 py-0.5 rounded">Double-click</span> any time slot to cycle its status:
              </p>
              <div className="flex flex-wrap gap-2 mt-3 text-sm">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-100 text-emerald-800 border border-emerald-200 font-semibold">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> Available
                </span>
                <span className="text-slate-400 self-center">→</span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-100 text-amber-900 border border-amber-300 font-semibold">
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-500" /> Tentative
                </span>
                <span className="text-slate-400 self-center">→</span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-100 text-red-800 border border-red-200 font-semibold">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-500" /> Blocked by me
                </span>
                <span className="text-slate-400 self-center">→</span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-100 text-blue-900 border border-blue-300 font-semibold">
                  <span className="w-2.5 h-2.5 rounded-full bg-blue-500" /> Manual Booked by me
                </span>
              </div>
              <p className="text-xs text-amber-900 mt-3 leading-snug">
                Patients see <strong>Tentative</strong> slots as still bookable. <strong>Blocked</strong> &amp; <strong>Manual Booked</strong> appear as unavailable. Click the small <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-slate-200 text-slate-700 text-[10px] font-bold">×</span> on any slot to delete just that slot for the day.
              </p>
            </div>
          </div>
        </div>
      )}

      {view === 'monthly' && (
        <>
          <div className="flex items-center justify-end">
            <button
              onClick={() => setShowRangeModal(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold hover:bg-rose-100"
              data-testid="slot-mark-leave-range-btn"
            >
              <Calendar size={12} /> Mark leave · date range
            </button>
          </div>
          <MonthlyCalendar
            year={currentMonth.year} month={currentMonth.month}
            schedules={schedules} getLocName={getLocName}
            onChangeMonth={changeMonth} onSelectDate={goToDay}
            selectedDate={selectedDate}
            leaveMap={leaveMap}
            leaveBusy={leaveBusy}
            onToggleLeave={toggleLeaveDay}
          />
        </>
      )}
      {showRangeModal && (
        <LeaveRangeModal
          onCancel={() => setShowRangeModal(false)}
          onApply={applyLeaveRange}
        />
      )}

      {view === 'daily' && (
        <DailySlotView
          selectedDate={selectedDate} setSelectedDate={setSelectedDate}
          onBack={() => setView('monthly')}
        />
      )}

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-[11px] text-slate-600 px-1" data-testid="slot-legend">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-100 border border-emerald-200 inline-block" /> Available</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-100 border border-amber-300 inline-block" /> Tentative</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-100 border border-red-200 inline-block" /> Blocked by me</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-100 border border-blue-300 inline-block" /> Manual Booked by me</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-sky-100 border border-sky-200 inline-block" /> Patient Booked</span>
      </div>

      {/* Bulk Apply */}
      <BulkApplySection />
    </div>
  );
}

function MonthlyCalendar({ year, month, schedules, getLocName, onChangeMonth, onSelectDate, selectedDate, leaveMap = {}, leaveBusy = {}, onToggleLeave }) {
  const firstDay = new Date(year, month - 1, 1);
  const daysInMonth = new Date(year, month, 0).getDate();
  let startPad = firstDay.getDay() - 1;
  if (startPad < 0) startPad = 6;
  const today = new Date().toISOString().split('T')[0];

  const cells = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const dateObj = new Date(year, month - 1, d);
    const dow = dateObj.getDay() === 0 ? 6 : dateObj.getDay() - 1;
    const dateStr = dateObj.toISOString().split('T')[0];
    const dayScheds = schedules
      .filter(s => s.day_of_week === dow && s.is_active !== false)
      .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
    const isPast = dateStr < today;
    const isOnLeave = dateStr in leaveMap;
    cells.push({ date: d, dateStr, dow, dayScheds, isToday: dateStr === today, isSelected: dateStr === selectedDate, isPast, isOnLeave, leaveBusy: !!leaveBusy[dateStr] });
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="monthly-calendar">
      {/* Month Navigation */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
        <button onClick={() => onChangeMonth(-1)} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500" data-testid="month-prev">
          <span className="text-lg">&#8249;</span>
        </button>
        <h4 className="text-base font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="month-title">
          {MONTH_NAMES[month - 1]} {year}
        </h4>
        <button onClick={() => onChangeMonth(1)} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500" data-testid="month-next">
          <span className="text-lg">&#8250;</span>
        </button>
      </div>

      {/* Day Headers */}
      <div className="grid grid-cols-7 border-b border-slate-100">
        {DAY_LABELS.map(d => (
          <div key={d} className="py-2 text-center text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{d}</div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7">
        {cells.map((cell, i) => (
          <div
            key={i}
            onClick={() => cell && onSelectDate(cell.dateStr)}
            className={`min-h-[80px] md:min-h-[100px] p-1.5 border-r border-b border-slate-100 last:border-r-0 transition-colors ${
              !cell ? 'bg-slate-50/50' :
              cell.isOnLeave ? 'bg-rose-50 ring-1 ring-rose-200 ring-inset cursor-pointer hover:bg-rose-100' :
              cell.isPast ? 'bg-slate-50 opacity-70 cursor-pointer hover:bg-slate-100' :
              cell.isSelected ? 'bg-sky-50 ring-2 ring-sky-400 ring-inset' :
              cell.isToday ? 'bg-amber-50/50 cursor-pointer' :
              'hover:bg-slate-50 cursor-pointer'
            }`}
            data-testid={cell ? `cal-day-${cell.dateStr}` : undefined}
            title={cell?.isPast ? 'View-only — see where you were on this day' : (cell?.isOnLeave ? 'Doctor on leave · untick to restore slots' : undefined)}
          >
            {cell && (
              <>
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs font-medium ${
                    cell.isToday ? 'bg-sky-600 text-white w-5 h-5 rounded-full flex items-center justify-center text-[10px]' :
                    cell.isPast ? 'text-slate-500' :
                    'text-slate-700'
                  }`}>
                    {cell.date}
                  </span>
                  {/* Per-day leave checkbox — only offered for TODAY or
                      future dates. Ticking flips the whole day into a
                      "Doctor On Leave" state end-to-end. */}
                  {!cell.isPast && onToggleLeave && (
                    <label
                      onClick={(e) => { e.stopPropagation(); onToggleLeave(cell.dateStr); }}
                      className={`flex items-center gap-0.5 cursor-pointer text-[8px] font-semibold uppercase tracking-wider select-none ${cell.isOnLeave ? 'text-rose-700' : 'text-slate-400 hover:text-rose-600'}`}
                      data-testid={`cal-leave-toggle-${cell.dateStr}`}
                    >
                      <input
                        type="checkbox"
                        checked={cell.isOnLeave}
                        readOnly
                        disabled={cell.leaveBusy}
                        className="w-2.5 h-2.5 accent-rose-500"
                      />
                      Leave
                    </label>
                  )}
                  {cell.isPast && (
                    <span className="text-[8px] text-slate-400 font-medium uppercase tracking-wide">past · view</span>
                  )}
                </div>
                {cell.isOnLeave ? (
                  <div className="rounded px-1 py-1 text-[9px] leading-tight border bg-rose-100 border-rose-300 text-rose-800 font-bold uppercase tracking-wider text-center" data-testid={`cal-on-leave-${cell.dateStr}`}>
                    Doctor On Leave
                  </div>
                ) : (
                  <>
                    {cell.dayScheds.length > 0 && !cell.isPast && (
                      <p className="text-[8px] text-sky-500 font-medium text-right -mt-1 mb-0.5">{cell.dayScheds.length} block{cell.dayScheds.length > 1 ? 's' : ''}</p>
                    )}
                    <div className="space-y-0.5">
                      {cell.dayScheds.map((s, j) => (
                        <div key={j} className={`rounded px-1 py-0.5 text-[8px] leading-tight border truncate ${
                          cell.isPast
                            ? 'bg-slate-100 border-slate-200 text-slate-500'
                            : (BLOCK_COLORS_M[s.block_type] || 'bg-slate-50 border-slate-200 text-slate-600')
                        }`}>
                          <span className="font-medium block truncate">{getLocName(s.location_id)}</span>
                          <span className="hidden sm:block">{s.start_time}-{s.end_time}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function DailySlotView({ selectedDate, setSelectedDate, onBack }) {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  // Per-day leave check — shows a red banner and disables slot editing
  // when the doctor has marked this specific date as leave.
  const [onLeave, setOnLeave] = useState(false);
  const [leaveBusy, setLeaveBusy] = useState(false);

  // Read-only when the doctor is viewing a past date — they can see where
  // they were but cannot change history.
  const todayStr = new Date().toISOString().split('T')[0];
  const isReadOnly = selectedDate < todayStr;

  const loadSlots = useCallback(() => {
    if (!selectedDate) return;
    setLoading(true);
    // Fetch leave status in parallel so the banner appears immediately
    // and slot editing is disabled without a flicker.
    api.get('/dashboard/leaves', { params: { from_date: selectedDate, to_date: selectedDate } })
      .then(r => setOnLeave((r.data.dates || []).includes(selectedDate)))
      .catch(() => setOnLeave(false));
    api.get('/dashboard/slots', { params: { date: selectedDate } })
      .then(r => setSlots(r.data))
      .catch(() => setSlots([]))
      .finally(() => setLoading(false));
  }, [selectedDate]);

  useEffect(() => { loadSlots(); }, [loadSlots]);

  const toggleThisDayLeave = async () => {
    if (isReadOnly || leaveBusy) return;
    setLeaveBusy(true);
    try {
      if (onLeave) {
        await api.delete(`/dashboard/leaves/${selectedDate}`);
        setOnLeave(false);
      } else {
        await api.post('/dashboard/leaves', { dates: [selectedDate] });
        setOnLeave(true);
      }
    } catch (_) { /* silent */ }
    finally { setLeaveBusy(false); }
  };

  const toggleSlot = async (locationId, startTime, currentStatus) => {
    if (isReadOnly) return;
    const cycle = { available: 'tentative', tentative: 'blocked', blocked: 'manual_booked', manual_booked: 'available' };
    const newStatus = cycle[currentStatus] || 'tentative';
    try {
      await api.post('/dashboard/slots/toggle', {
        date: selectedDate, location_id: locationId, start_time: startTime, status: newStatus
      });
      setSlots(prev => prev.map(loc => {
        if (loc.location_id !== locationId) return loc;
        return { ...loc, slots: loc.slots.map(s => s.start_time === startTime ? { ...s, status: newStatus } : s) };
      }));
    } catch { /* silent */ }
  };

  const deleteSlot = async (locationId, startTime) => {
    if (isReadOnly) return;
    if (!window.confirm('Delete this time slot from this day only? You can re-add it from the Schedule tab.')) return;
    try {
      await api.post('/dashboard/slots/toggle', {
        date: selectedDate, location_id: locationId, start_time: startTime, status: 'deleted'
      });
      setSlots(prev => prev.map(loc => {
        if (loc.location_id !== locationId) return loc;
        return { ...loc, slots: loc.slots.filter(s => s.start_time !== startTime) };
      }));
    } catch { /* silent */ }
  };

  const dt = new Date(selectedDate + 'T00:00:00');
  const dayName = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][dt.getDay()];

  // Prev/Next day nav
  const navDay = (dir) => {
    const d = new Date(selectedDate + 'T00:00:00');
    d.setDate(d.getDate() + dir);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  return (
    <div className="space-y-4">
      {/* Day Header */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid="daily-header">
        <div className="flex items-center justify-between">
          <button onClick={onBack} className="text-xs text-sky-600 hover:text-sky-700 font-medium" data-testid="back-to-monthly">
            &#8249; Back to Month
          </button>
          <div className="flex items-center gap-2">
            <button onClick={() => navDay(-1)} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500" data-testid="day-prev">&#8249;</button>
            <div className="text-center min-w-[140px]">
              <p className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="daily-date-title">{dayName}</p>
              <p className="text-xs text-slate-500">{dt.toLocaleDateString('en', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
            </div>
            <button onClick={() => navDay(1)} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500" data-testid="day-next">&#8250;</button>
          </div>
          <div className="w-20" />
        </div>
      </div>

      {/* Read-only banner for past dates */}
      {isReadOnly && (
        <div className="bg-slate-100 border-2 border-slate-300 rounded-2xl p-4 flex items-start gap-3" data-testid="daily-readonly-banner">
          <div className="w-9 h-9 rounded-xl bg-slate-200 flex items-center justify-center shrink-0">
            <Lock size={16} className="text-slate-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-900">View-only · past date</p>
            <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">
              You can see where you were on this day, but you can&apos;t change slots from the past. To update upcoming days, navigate to today or any future date.
            </p>
          </div>
        </div>
      )}

      {/* Leave toggle — future dates only. Ticked = all slots on this day
          collapse into a "Doctor On Leave" banner for both dashboard
          and public site consumers. */}
      {!isReadOnly && (
        <div className={`rounded-2xl p-4 flex items-center gap-3 ${onLeave ? 'bg-rose-50 border-2 border-rose-300' : 'bg-white border border-slate-200'}`} data-testid="daily-leave-toggle-row">
          <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
            <input
              type="checkbox"
              checked={onLeave}
              onChange={toggleThisDayLeave}
              disabled={leaveBusy}
              className="w-4 h-4 accent-rose-500"
              data-testid="daily-leave-checkbox"
            />
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-semibold ${onLeave ? 'text-rose-800' : 'text-slate-800'}`}>
                {onLeave ? 'Doctor On Leave · this day' : 'Mark this day as leave'}
              </p>
              <p className="text-[11px] text-slate-500 mt-0.5">
                {onLeave
                  ? 'All slots hidden on your public site. Untick to restore.'
                  : 'Tick to hide every clinic slot on this day and show "Doctor On Leave" to patients.'}
              </p>
            </div>
          </label>
          {leaveBusy && <Loader2 size={14} className="animate-spin text-rose-500" />}
        </div>
      )}

      {/* Timeline — hidden when the day is marked as leave so patients
          + the doctor both see a unified "Doctor On Leave" state. */}
      {onLeave && !isReadOnly ? (
        <div className="bg-rose-50 border-2 border-rose-300 rounded-2xl p-8 text-center" data-testid="daily-on-leave-panel">
          <div className="w-14 h-14 mx-auto rounded-full bg-rose-100 border border-rose-300 flex items-center justify-center mb-3">
            <Calendar size={22} className="text-rose-600" />
          </div>
          <p className="text-lg font-bold text-rose-800" style={{ fontFamily: 'Outfit, sans-serif' }}>Doctor On Leave</p>
          <p className="text-xs text-rose-700 mt-1">Every location and slot on this day is hidden from patients. Untick the checkbox above to restore your normal schedule.</p>
        </div>
      ) : loading ? (
        <div className="py-10 text-center"><Loader2 className="animate-spin text-sky-600 mx-auto" /></div>
      ) : slots.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center" data-testid="no-slots-daily">
          <Calendar size={32} className="text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">No schedule on {dayName}. Set up your weekly schedule first.</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="daily-timeline">
          {slots.map(loc => {
            const stats = { available: 0, tentative: 0, blocked: 0, booked: 0 };
            loc.slots.forEach(s => { stats[s.status] = (stats[s.status] || 0) + 1; });
            return (
              <div key={loc.location_id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid={`daily-block-${loc.location_id}`}>
                {/* Block Header */}
                <div className={`px-5 py-3 border-b ${BLOCK_COLORS_M[loc.block_type] || 'bg-slate-50 border-slate-200'}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold">{loc.location_name}</p>
                      <p className="text-[10px] opacity-80">{loc.location_address}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-medium">{loc.schedule_start} - {loc.schedule_end}</p>
                      <p className="text-[10px] opacity-80 capitalize">{loc.block_type} | {loc.slot_duration}min slots</p>
                    </div>
                  </div>
                  {/* Mini Stats */}
                  <div className="flex gap-3 mt-2 text-[10px]">
                    <span className="flex items-center gap-1"><span className={`w-2 h-2 rounded-full ${STATUS_DOT.available}`} /> {stats.available} avail</span>
                    {stats.tentative > 0 && <span className="flex items-center gap-1"><span className={`w-2 h-2 rounded-full ${STATUS_DOT.tentative}`} /> {stats.tentative} tentative</span>}
                    {stats.blocked > 0 && <span className="flex items-center gap-1"><span className={`w-2 h-2 rounded-full ${STATUS_DOT.blocked}`} /> {stats.blocked} blocked</span>}
                    {stats.booked > 0 && <span className="flex items-center gap-1"><span className={`w-2 h-2 rounded-full ${STATUS_DOT.booked}`} /> {stats.booked} booked</span>}
                  </div>
                </div>

                {/* Slot Grid - optimized for touch */}
                <div className="p-4">
                  <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-1.5">
                    {loc.slots.map(slot => {
                      const isBooked = slot.status === 'booked';
                      const interactionsDisabled = isBooked || isReadOnly;
                      return (
                        <div key={slot.start_time} className="relative group">
                          <button
                            onDoubleClick={() => !interactionsDisabled && toggleSlot(loc.location_id, slot.start_time, slot.status)}
                            onClick={(e) => {
                              // Mobile-friendly: tap counts as "double click" via timer; for desktop the dblclick handler fires
                              if (interactionsDisabled) return;
                              const now = Date.now();
                              if (e.currentTarget.__lastTap && now - e.currentTarget.__lastTap < 350) {
                                toggleSlot(loc.location_id, slot.start_time, slot.status);
                                e.currentTarget.__lastTap = 0;
                              } else {
                                e.currentTarget.__lastTap = now;
                              }
                            }}
                            className={`w-full py-2.5 sm:py-2 rounded-xl text-xs font-medium border transition-all text-center ${STATUS_COLORS[slot.status] || STATUS_COLORS.available} ${isReadOnly ? 'opacity-70 cursor-not-allowed' : ''}`}
                            title={isReadOnly ? `${STATUS_LABEL[slot.status] || slot.status} — view-only (past date)` : isBooked ? `Booked: ${slot.booking?.patient_name}` : `${STATUS_LABEL[slot.status] || slot.status} — double-click to change`}
                            data-testid={`slot-toggle-${loc.location_id}-${slot.start_time}`}
                          >
                            <span className="block text-[11px] sm:text-xs font-semibold">{slot.start_time}</span>
                            <span className="hidden sm:block text-[8px] opacity-70 mt-0.5">{slot.end_time}</span>
                          </button>
                          {!isBooked && !isReadOnly && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); deleteSlot(loc.location_id, slot.start_time); }}
                              className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white border border-slate-300 text-slate-500 hover:bg-red-500 hover:text-white hover:border-red-500 shadow-sm flex items-center justify-center text-[11px] font-bold leading-none opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Delete this slot for this day only"
                              data-testid={`slot-delete-${loc.location_id}-${slot.start_time}`}
                            >
                              ×
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function BulkApplySection() {
  const [bulkStart, setBulkStart] = useState('');
  const [bulkEnd, setBulkEnd] = useState('');
  const [bulkMsg, setBulkMsg] = useState('');

  const handleBulkApply = async () => {
    if (!bulkStart || !bulkEnd) return;
    setBulkMsg('');
    try {
      const { data } = await api.post('/dashboard/slots/bulk-apply', { start_date: bulkStart, end_date: bulkEnd });
      setBulkMsg(data.message);
    } catch (e) { setBulkMsg('Error: ' + formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="bulk-apply-section">
      <h4 className="text-sm font-medium text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Apply Weekly Template to Period</h4>
      <p className="text-xs text-slate-500 mb-3">Reset all slot overrides in a date range so the weekly pattern applies cleanly.</p>
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1 block">Start Date</label>
          <input type="date" className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none" value={bulkStart} onChange={e => setBulkStart(e.target.value)} data-testid="bulk-start" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1 block">End Date</label>
          <input type="date" className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none" value={bulkEnd} onChange={e => setBulkEnd(e.target.value)} data-testid="bulk-end" />
        </div>
        <button onClick={handleBulkApply} disabled={!bulkStart || !bulkEnd} className="rounded-full px-5 py-2 bg-sky-600 text-white text-sm font-medium hover:bg-sky-700 disabled:opacity-50" data-testid="bulk-apply-button">
          Apply Template
        </button>
      </div>
      {bulkMsg && <p className={`text-sm mt-2 ${bulkMsg.startsWith('Error') ? 'text-red-600' : 'text-emerald-600'}`}>{bulkMsg}</p>}
    </div>
  );
}



// ─────────────────────────────────────────────────────────────────────────
// Bulk "Mark leave · date range" modal — companion to the per-day
// checkbox above. Server expands the range and idempotently upserts
// every date in [from, to]. Server also caps the range at 92 days.
// ─────────────────────────────────────────────────────────────────────────
function LeaveRangeModal({ onCancel, onApply }) {
  const today = new Date().toISOString().slice(0, 10);
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [busy, setBusy] = useState(false);

  const span = (() => {
    try {
      const a = new Date(fromDate + 'T00:00:00');
      const b = new Date(toDate + 'T00:00:00');
      return Math.round((b - a) / 86400000) + 1;
    } catch { return 0; }
  })();
  const valid = fromDate && toDate && span > 0 && span <= 92;

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    try { await onApply(fromDate, toDate); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-end sm:items-center justify-center p-4" onClick={onCancel} data-testid="leave-range-modal">
      <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-4 border-b border-slate-100">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-rose-700">Doctor on Leave</p>
            <h3 className="text-base font-bold text-slate-900 mt-0.5">Mark leave · date range</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">Every date in the range flips to &quot;Doctor On Leave&quot;.</p>
          </div>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-600" data-testid="leave-range-close">
            <X size={18} />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <label className="block">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">From</span>
            <input type="date" value={fromDate} min={today} onChange={(e) => setFromDate(e.target.value)}
              className="w-full mt-1 rounded-xl border border-slate-300 px-3 py-2 text-sm" data-testid="leave-range-from" />
          </label>
          <label className="block">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">To</span>
            <input type="date" value={toDate} min={fromDate || today} onChange={(e) => setToDate(e.target.value)}
              className="w-full mt-1 rounded-xl border border-slate-300 px-3 py-2 text-sm" data-testid="leave-range-to" />
          </label>
          {span > 0 && (
            <p className={`text-[11px] font-semibold ${span > 92 ? 'text-rose-600' : 'text-slate-600'}`}>
              {span} {span === 1 ? 'day' : 'days'}{span > 92 ? ' · exceeds 92-day cap' : ''}
            </p>
          )}
          <button onClick={submit} disabled={!valid || busy}
            className="w-full py-2.5 rounded-xl bg-rose-600 text-white font-bold hover:bg-rose-700 disabled:opacity-60 flex items-center justify-center gap-2"
            data-testid="leave-range-apply">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Calendar size={14} />}
            {busy ? 'Marking…' : 'Mark all these days as leave'}
          </button>
          <p className="text-[10px] text-slate-400 text-center">Untick individual days later from the calendar to restore.</p>
        </div>
      </div>
    </div>
  );
}
